import { RTKQueryTag } from 'src/common/constants';
import type { ApiResponse } from 'src/common/interfaces';
import type { GenerateRandomAvatarResponse } from 'src/common/interfaces/manage-room';
import type {
  CreateTeamPayload,
  CurrentTeamResponse,
  IsLockedInRoomResponse,
  RemoveMemberPayload,
  UpdateTeamPayload,
} from 'src/common/interfaces/teams';
import { userSelfQueryTag } from 'src/common/slices/index';
import { apiSlice } from 'src/features/api/api-slice';

const AllTeamDetails = [userSelfQueryTag, RTKQueryTag.Teams];
export const teamsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createTeam: builder.mutation<ApiResponse<string>, CreateTeamPayload>({
      query: (payload) => ({
        url: 'teams/create',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: AllTeamDetails,
    }),

    getCurrentTeam: builder.query<ApiResponse<CurrentTeamResponse>, void>({
      query: () => ({
        url: 'teams/current',
      }),
      providesTags: [RTKQueryTag.Teams],
    }),

    removeTeamMember: builder.mutation<ApiResponse<void>, RemoveMemberPayload>({
      query: (payload) => ({
        url: 'teams/remove-member',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [RTKQueryTag.Teams],
    }),

    leaveTeam: builder.mutation<ApiResponse<void>, string>({
      query: (teamCode) => ({
        url: 'teams/leave',
        method: 'POST',
        params: { teamCode },
      }),
      invalidatesTags: AllTeamDetails,
    }),

    joinTeam: builder.mutation<ApiResponse<void>, string>({
      query: (token) => ({
        url: 'teams/join-team',
        method: 'GET',
        params: { token },
      }),
      invalidatesTags: AllTeamDetails,
    }),

    updateTeam: builder.mutation<ApiResponse<string>, UpdateTeamPayload>({
      query: (payload) => ({
        url: 'teams/update',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [RTKQueryTag.Teams],
    }),

    deleteTeam: builder.mutation<ApiResponse<string>, string>({
      query: (teamCode) => ({
        url: 'teams/delete',
        method: 'DELETE',
        params: { teamCode },
      }),
      invalidatesTags: AllTeamDetails,
    }),

    generateRandomTeamAvatar: builder.mutation<ApiResponse<GenerateRandomAvatarResponse>, void>({
      query: () => ({
        url: `teams/avatar/random`,
      }),
    }),

    isCurrentTeamOwner: builder.query<ApiResponse<boolean>, void>({
      query: () => ({
        url: 'teams/is-owner',
      }),
    }),

    isLockedInRoom: builder.query<ApiResponse<IsLockedInRoomResponse>, string>({
      query: (roomCode) => ({
        url: 'teams/is-locked-in-room',
        method: 'GET',
        params: { roomCode },
      }),
    }),
  }),
});

export const {
  useCreateTeamMutation,
  useGetCurrentTeamQuery,
  useRemoveTeamMemberMutation,
  useLeaveTeamMutation,
  useJoinTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useGenerateRandomTeamAvatarMutation,
  useIsCurrentTeamOwnerQuery,
  useIsLockedInRoomQuery,
} = teamsApiSlice;
